"use client";

import { useState } from "react";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { X, ChevronLeft, ChevronRight } from "lucide-react";
import { Button } from "@/components/ui/button";
const galleryImages = [{
  id: 1,
  src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1509250/img/supper_club_intimate_dining.png",
  alt: "Pass the Plate supper club intimate dining experience"
}, {
  id: 2,
  src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1509250/img/cooking_class_hands_on_learning.png",
  alt: "Create the Plate cooking class hands-on learning"
}, {
  id: 3,
  src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1509250/img/curated_menu_plated_dish.png",
  alt: "Beautifully plated curated menu dish"
}, {
  id: 4,
  src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1509250/img/community_gathering_shared_meal.png",
  alt: "Community gathering around a shared meal"
}, {
  id: 5,
  src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1509250/img/culinary_techniques_demonstration.png",
  alt: "Chef demonstrating culinary techniques"
}, {
  id: 6,
  src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1509250/img/fresh_ingredients_preparation.png",
  alt: "Fresh ingredients being prepared for cooking"
}];
export default function Gallery() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll({
    once: true,
    threshold: 0.1
  });
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [currentIndex, setCurrentIndex] = useState(0);
  const openLightbox = (index: number) => {
    setCurrentIndex(index);
    setLightboxOpen(true);
  };
  const closeLightbox = () => {
    setLightboxOpen(false);
  };
  const navigatePrev = () => {
    setCurrentIndex(prev => prev === 0 ? galleryImages.length - 1 : prev - 1);
  };
  const navigateNext = () => {
    setCurrentIndex(prev => prev === galleryImages.length - 1 ? 0 : prev + 1);
  };
  const handleKeyDown = (e: KeyboardEvent) => {
    if (!lightboxOpen) return;
    if (e.key === "Escape") closeLightbox();
    if (e.key === "ArrowLeft") navigatePrev();
    if (e.key === "ArrowRight") navigateNext();
  };
  useState(() => {
    if (typeof window !== "undefined") {
      window.addEventListener("keydown", handleKeyDown as any);
      return () => window.removeEventListener("keydown", handleKeyDown as any);
    }
  });
  return <section ref={ref} className="py-16 px-4 md:py-24 bg-background" aria-label="Gallery">
      <div className="container mx-auto max-w-7xl">
        <div className={`text-center mb-12 transition-all duration-[var(--duration-fast)] ease-[var(--ease-smooth)] ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          <h2 className="text-4xl md:text-5xl font-display font-bold text-foreground mb-4">
            Gallery
          </h2>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
            Explore moments from our supper clubs and cooking classes where food
            brings people together
          </p>
        </div>

        <div className={`gallery transition-all duration-[var(--duration-fast)] ease-[var(--ease-smooth)] delay-100 ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          {galleryImages.map((image, i) => <div key={image.id} data-index={i} className="gallery-item cursor-pointer" onClick={() => openLightbox(i)} onKeyDown={e => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            openLightbox(i);
          }
        }} role="button" tabIndex={0} aria-label={`View ${image.alt} in lightbox`}>
              <img src={image.src} alt={image.alt} className="w-full h-full object-cover" />
            </div>)}
        </div>
      </div>

      {lightboxOpen && <div className="gallery-lightbox" onClick={closeLightbox} role="dialog" aria-modal="true" aria-label="Image lightbox">
          <Button variant="ghost" size="icon" className="absolute top-4 right-4 size-10 bg-background/80 hover:bg-background text-foreground rounded-full shadow-medium z-10" onClick={closeLightbox} aria-label="Close lightbox">
            <X className="size-6" />
          </Button>

          <Button variant="ghost" size="icon" className="absolute left-4 top-1/2 -translate-y-1/2 size-10 bg-background/80 hover:bg-background text-foreground rounded-full shadow-medium z-10" onClick={e => {
        e.stopPropagation();
        navigatePrev();
      }} aria-label="Previous image">
            <ChevronLeft className="size-6" />
          </Button>

          <Button variant="ghost" size="icon" className="absolute right-4 top-1/2 -translate-y-1/2 size-10 bg-background/80 hover:bg-background text-foreground rounded-full shadow-medium z-10" onClick={e => {
        e.stopPropagation();
        navigateNext();
      }} aria-label="Next image">
            <ChevronRight className="size-6" />
          </Button>

          <img src={galleryImages[currentIndex].src} alt={galleryImages[currentIndex].alt} onClick={e => e.stopPropagation()} className="max-w-[90vw] max-h-[90vh] rounded-lg shadow-strong object-contain" />

          <p className="absolute bottom-8 left-1/2 -translate-x-1/2 text-dark-foreground bg-dark-background/80 px-4 py-2 rounded-lg text-sm">
            {currentIndex + 1} / {galleryImages.length}
          </p>
        </div>}
    </section>;
}